import React from 'react';
import { Box, Container, Typography, alpha, useTheme } from '@mui/material';
import { 
  Biotech as HealthcareIcon,
  Science as ScienceIcon,
  HealthAndSafety as SafetyIcon,
  LocalHospital as HospitalIcon
} from '@mui/icons-material';

/**
 * Hero-style Login Background Component
 * A darker version of the splash page hero for authentication pages
 */
export const LoginHero = ({ children }) => {
  const theme = useTheme();
  
  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        position: 'relative',
        // Darker gradient than homepage
        background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 30%, #334155 70%, #1e293b 100%)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          // Subtle medical-themed gradient overlay
          background: 'radial-gradient(ellipse at top left, rgba(13, 148, 136, 0.15) 0%, transparent 50%), radial-gradient(ellipse at bottom right, rgba(92, 114, 92, 0.15) 0%, transparent 50%)',
          zIndex: 0
        }
      }}
    >
      {/* Animated Medical Background Elements */}
      <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0 }}>
        {/* Large Background Circle */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: '-20%',
            right: '-10%',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: alpha(theme.palette.primary.main, 0.03),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            animation: 'float 20s ease-in-out infinite'
          }}
        />
        
        {/* Medium Background Circle */}
        <Box 
          sx={{ 
            position: 'absolute',
            bottom: '-15%',
            left: '-5%',
            width: 400,
            height: 400,
            borderRadius: '50%',
            background: alpha(theme.palette.secondary.main, 0.03),
            border: `1px solid ${alpha(theme.palette.secondary.main, 0.08)}`,
            animation: 'float 15s ease-in-out infinite reverse'
          }}
        />
        
        {/* Small Floating Icons */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: '20%',
            left: '10%',
            width: 60,
            height: 60,
            borderRadius: 2,
            background: alpha('#0d9488', 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'orbit 30s linear infinite',
            boxShadow: `0 8px 24px ${alpha('#0d9488', 0.2)}`
          }}
        >
          <ScienceIcon sx={{ color: '#0d9488', fontSize: 28, opacity: 0.7 }} />
        </Box>
        
        <Box 
          sx={{ 
            position: 'absolute',
            top: '60%',
            right: '15%',
            width: 50,
            height: 50,
            borderRadius: 2,
            background: alpha('#5c725c', 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'orbit 25s linear infinite reverse',
            boxShadow: `0 8px 24px ${alpha('#5c725c', 0.2)}`
          }}
        >
          <SafetyIcon sx={{ color: '#5c725c', fontSize: 24, opacity: 0.7 }} />
        </Box>
        
        <Box 
          sx={{ 
            position: 'absolute',
            bottom: '30%',
            left: '20%',
            width: 55,
            height: 55,
            borderRadius: '50%',
            background: alpha('#14b8a6', 0.08),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'float 18s ease-in-out infinite',
            boxShadow: `0 8px 24px ${alpha('#14b8a6', 0.2)}`
          }}
        >
          <HospitalIcon sx={{ color: '#14b8a6', fontSize: 26, opacity: 0.7 }} />
        </Box>
        
        {/* DNA Helix Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '120%',
            height: '120%',
            opacity: 0.02,
            background: `repeating-linear-gradient(
              45deg,
              transparent,
              transparent 20px,
              ${theme.palette.primary.main} 20px,
              ${theme.palette.primary.main} 21px
            )`,
            animation: 'slide 50s linear infinite'
          }}
        />
      </Box>
      
      {/* Main Content Container */}
      <Container maxWidth={false} sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Top Header Area */}
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800,
              color: 'white',
              mb: 1,
              letterSpacing: '-0.02em',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            UnisynHub
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: alpha('#ffffff', 0.8),
              fontWeight: 400
            }}
          >
            Regenerative Medicine Management Platform
          </Typography>
        </Box>
        
        {/* Center Content Area */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          py: 4
        }}>
          {children}
        </Box>
        
        {/* Bottom Trust Indicators */}
        <Box sx={{ py: 3, textAlign: 'center' }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: alpha('#ffffff', 0.5),
              fontSize: '0.875rem'
            }}
          >
            Trusted by leading healthcare providers nationwide
          </Typography>
        </Box>
      </Container>
      
      {/* CSS Animations */}
      <style jsx global>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes orbit {
          from { transform: rotate(0deg) translateX(100px) rotate(0deg); }
          to { transform: rotate(360deg) translateX(100px) rotate(-360deg); }
        }
        
        @keyframes slide {
          0% { transform: translate(-50%, -50%) translateX(0); }
          100% { transform: translate(-50%, -50%) translateX(100px); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </Box>
  );
};
